import Div from "@jumbo/shared/Div/Div";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import InputLabel from "@mui/material/InputLabel";
import "./AddNewForm.css";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import {useFormik} from "formik";
import {useState, useEffect, useRef} from "react";
import {toast} from "react-toastify";
import * as Yup from "yup";
import {post} from "../../common/network";
import {categories, categoriesSubCategoriesMap} from "../../constants/category_mappings";
import {DateFormatsOptions, VALIDATION_MESSAGE} from "../../constants/index";
import UploadForm from "./UploadForm";
import { fetchCountryData } from "../../api/countries";
import dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Typography from "@mui/material/Typography";
import SubjectLinesModal from "../../components/common/modal-preview/SubjectLineModal";

const AddNewForm = () => {
    const [uploadedForm, setUploadedForm] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [countriesMapping, setCountriesMapping] = useState([]);
    const inputRef = useRef(null);
    const [usedChips, setUsedChips] = useState([]);
    const [openModal, setOpenModal] = useState(false);

    const today = dayjs();
    useEffect(() => {
        fetchCountryMapping();
    }, []);
    // Validation schema defined
    const validationSchema = Yup.object({
        form_code: Yup.string().required(VALIDATION_MESSAGE.required),
        form_name: Yup.string().required(VALIDATION_MESSAGE.required)
    });

    const presetOptions = [
        "Application Number",
        "Attorney Docket Number",
        "Filing Date",
        "Publication Date",
        "Status"
    ];

    const additionalOptions = [
        "Agent Reference Number",
        "Client Reference Number",
        "Correspondence Customer Number",
        "Country Code",
        "Country Name",
        "Grant Date",
        "Local Filing Date",
        "Matter Entity Size",
        "Matter Sub-Matter Type",
        "Matter Type",
        "Patent Number",
        "Pct Application Number",
        "Pct Date",
        "Priority Application Number",
        "Priority Date",
        "Publication Number",
        "Title",
        "Billing Reference Number",
        ...(localStorage.getItem("TENANT_NAME") === "knobbe" ? [
            "Trademark",
            "Mark Type",
            "Intent to Use",
            "Last Renewal Date",
            "First Use",
            "First Use in Commerce",
            "Allowance"
        ] : [])
    ];

    const sortedOptions = [...additionalOptions].sort((a, b) =>
        a.localeCompare(b)
    );

    const tokenize = (value) => {
        const matches = value.match(/\[([^\]]+)\]/g);
        return matches ? matches.map(v => v.replace(/[\[\]]/g, "").trim()) : [];
    };

    // Added formik for form and fields handling
    const formik = useFormik({
        initialValues: {
            form_code: "",
            form_name: "",
            filing_name: null,
            category: null,
            sub_category: null,
            country: null,
            dateFormat: "YYYY-MM-DD",
            subjectLines: "" 
        },
        validationSchema: validationSchema,
        onSubmit: async (values, {resetForm}) => {
            try {
                const formData = new FormData();
                formData.append("form_code", values.form_code);
                formData.append("form_name", values.form_name);
                formData.append("filing_name", values.filing_name || null);
                formData.append("default_category", values.category);
                formData.append("default_sub_category", values.sub_category);
                formData.append("status", "Active");
                formData.append("form_type", "custom");
                formData.append("sign_required", false);
                if (values.country) {
                    formData.append("country", values.country);
                }
                formData.append("dateFormat", values.dateFormat);
                formData.append("subject_lines", values.subjectLines || "");

                if (uploadedForm) {
                    const file = uploadedForm.file; // Assuming uploadedForm contains the file object
                    formData.append("file", file, file.name);
                }
                setIsLoading(true);
                const response = await post(`docgen/forms/custom`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                });

                if (response?.success === true) {
                    toast.success(response.message);
                    setUploadedForm(null);
                    resetForm(); // Reset the formik form
                }
            } catch (error) {
            } finally {
                setIsLoading(false);
            }
        }
    });
    const lastValidValue = useRef(formik.values.subjectLines || "");
    //#endregion

    //#region for function
    /**
     * Upload handler function for uploading matters
     * @param {Array} files Files object
     */
    const uploadHandler = (files) => {
        if (files) {
            const file = files[0];
            const fileName = file.name;
            const fileSize = (file.size / 1024).toFixed(2);
            setUploadedForm({
                file: file, // Store the file object for later use
                label: `${fileName} - ${fileSize}kb`
            });
        }
    };
    //#endregion

    const fetchCountryMapping = async () => {
        const data = await fetchCountryData();
        if (data) {
            const countries = data
                .map(country => ({
                    label: country.name,
                    value: country.code,
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            setCountriesMapping(countries);
        }
    };

    const handleChipClick = (option) => {
        if (usedChips.includes(option)) return;

        const token = `[${option}]`;
        const input = inputRef.current;
        let value = formik.values.subjectLines || "";

        const start = input?.selectionStart ?? value.length;
        const before = value.slice(0, start);
        const after = value.slice(start);

        const needsSpaceBefore =
            before.length > 0 && !before.endsWith(" ");
        const needsSpaceAfter =
            after.length > 0 && !after.startsWith(" ");

        const updated =
            `${before}${needsSpaceBefore ? " " : ""}` +
            `${token}` +
            `${needsSpaceAfter ? " " : ""}${after}`;

        formik.setFieldValue("subjectLines", updated);
        setUsedChips(prev => [...prev, option]);
        lastValidValue.current = updated;
    };

    const handleChange = (e) => {
        formik.setFieldValue("subjectLines", e.target.value);
        lastValidValue.current = e.target.value;
    };

    useEffect(() => {
        const tokens = tokenize(formik.values.subjectLines || "");
        setUsedChips(tokens);
        lastValidValue.current = formik.values.subjectLines || "";
    }, [formik.values.subjectLines]);

    useEffect(() => {
        formik.setFieldValue("subjectLines",formik.values.form_name || "");
    }, [formik.values.form_name]);


    const handleKeyDown = (e) => {
        const input = inputRef.current;
        if (!input) return;

        const value = formik.values.subjectLines || "";
        const cursorPos = input.selectionStart;

        const tokenRegex = /\[[^\[\]]+\]/g;
        let match;

        const navigationKeys = [
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Home",
            "End",
            "Tab"
        ];

        //make keys non-functional if inside the placeholder
        while ((match = tokenRegex.exec(value)) !== null) {
            const start = match.index;
            const end = start + match[0].length;

            // strictly inside (not at boundaries)
            if (cursorPos > start && cursorPos < end) {

                //allow navigation 
                if (navigationKeys.includes(e.key)) {
                    return;
                }
                e.preventDefault();
                return;
            }
        }
        
        // Handle Backspace key only if just after placeholder
        if (e.key === "Backspace") {
            const leftPart = value.slice(0, cursorPos);
            const rightPart = value.slice(cursorPos);

            const tokenMatch = leftPart.match(/\[[^\[\]]+\]$/);

            if (tokenMatch) {
                const tokenText = tokenMatch[0].replace(/[\[\]]/g, "").trim();

                if (usedChips.includes(tokenText)) {
                    const newValue =
                        value.slice(0, leftPart.length - tokenMatch[0].length) +
                        rightPart;

                    formik.setFieldValue("subjectLines", newValue);

                    setUsedChips(prev =>
                        prev.filter(t => t !== tokenText)
                    );

                    const newCursor =
                        leftPart.length - tokenMatch[0].length;

                    setTimeout(() => {
                        input.setSelectionRange(newCursor, newCursor);
                    }, 0);

                    e.preventDefault();
                    return;
                }
            }
        }
        // Handle Delete (only if just before placeholder)
        if (e.key === "Delete") {
            const leftPart = value.slice(0, cursorPos);
            const rightPart = value.slice(cursorPos);

            const tokenMatch = rightPart.match(/^\[[^\[\]]+\]/);

            if (tokenMatch) {
                const tokenText = tokenMatch[0].replace(/[\[\]]/g, "").trim();

                if (usedChips.includes(tokenText)) {
                    const newValue =
                        leftPart + rightPart.slice(tokenMatch[0].length);

                    formik.setFieldValue("subjectLines", newValue);

                    setUsedChips(prev =>
                        prev.filter(t => t !== tokenText)
                    );

                    setTimeout(() => {
                        input.setSelectionRange(cursorPos, cursorPos);
                    }, 0);

                    e.preventDefault();
                    return;
                }
            }
        }
    };

    const handleCheckboxChange = (option, checked) => {
        const token = `[${option}]`;
        const input = inputRef.current;
        let value = formik.values.subjectLines || "";

        if (checked) {
            if (usedChips.includes(option)) return;

            const start = input?.selectionStart ?? value.length;
            const before = value.slice(0, start);
            const after = value.slice(start);

            // Ensure single space separation
            const needsSpaceBefore =
                before.length > 0 && !before.endsWith(" ");
            const needsSpaceAfter =
                after.length > 0 && !after.startsWith(" ");

            const updated =
                `${before}${needsSpaceBefore ? " " : ""}` +
                `${token}` +
                `${needsSpaceAfter ? " " : ""}${after}`;

            formik.setFieldValue("subjectLines", updated);

            setUsedChips(prev => [...prev, option]);
        } else {
            const updated = value
                .replace(token, "")
                .replace(/\s{2,}/g, " ")
                .trim();

            formik.setFieldValue("subjectLines", updated);

            setUsedChips(prev => prev.filter(t => t !== option));
        }
    };

    return (
        <div className="page-content-wrapper">
            <Box className="w-100">
                {isLoading ? (
                    <Div
                        sx={{
                            width: "100%",
                            height: "100%",
                            minHeight: "60vh",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            ".btn-loader-icon": {
                                width: "60px",
                                height: "60px",
                                border: "5px solid gray",
                                borderColor: "transparent gray gray gray",
                                borderRadius: "50%",
                                animation: "rotate .5s linear infinite"
                            }
                        }}
                    >
                        <div className="btn-loader-icon"></div>
                    </Div>
                ) : (
                    <Div
                        sx={{
                            pt: "17px"
                        }}
                    >
                        <form onSubmit={formik.handleSubmit}>
                            <div className="container">
                                <div className="row">
                                    <div className="col">
                                        <div className="form-group">
                                            <label
                                                htmlFor="upload_file"
                                                className="upload_file_label"
                                            >
                                                Upload Template File
                                            </label>
                                            <UploadForm onUpload={uploadHandler} formats={["docx"]} count={1}/>
                                            {uploadedForm && (
                                                <div className="mt-3">
                                                    <Chip
                                                        icon={
                                                            <AttachFileOutlinedIcon
                                                                sx={{
                                                                    fontSize: "18px"
                                                                }}
                                                            />
                                                        }
                                                        className="uploaded-form-details"
                                                        label={uploadedForm.label}
                                                        onDelete={() => setUploadedForm(null)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-100"></div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="form_code">Form Code</InputLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                id="form_code"
                                                name="form_code"
                                                value={formik.values.form_code}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.form_code &&
                                                    Boolean(formik.errors.form_code)
                                                }
                                                helperText={
                                                    formik.touched.form_code && formik.errors.form_code
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="form_name">Form Name</InputLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                id="form_name"
                                                name="form_name"
                                                value={formik.values.form_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.form_name &&
                                                    Boolean(formik.errors.form_name)
                                                }
                                                helperText={
                                                    formik.touched.form_name && formik.errors.form_name
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="filing_name">Filing Name</InputLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                id="filing_name"
                                                name="filing_name"
                                                value={formik.values.filing_name || null}
                                                onChange={formik.handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="category">Category</InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select
                                                    id="category"
                                                    name="category"
                                                    value={formik.values.category}
                                                    onChange={formik.handleChange}
                                                    displayEmpty
                                                    renderValue={formik.values.category ? undefined : () => "Select Category"}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: {
                                                            maxHeight: "40vh",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value={null}>Select Category</MenuItem>
                                                    {categories?.map((category) => (
                                                        <MenuItem key={category} value={category}>{category}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="sub_category">Sub-Category</InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select
                                                    id="sub_category"
                                                    name="sub_category"
                                                    value={formik.values.sub_category}
                                                    onChange={formik.handleChange}
                                                    displayEmpty
                                                    renderValue={formik.values.sub_category ? undefined : () => "Select Sub-category"}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: {
                                                            maxHeight: "40vh",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value={null}>Select Sub-category</MenuItem>
                                                    {categoriesSubCategoriesMap[formik.values.category]?.map((sub_category) => (
                                                        <MenuItem key={sub_category} value={sub_category}>{sub_category}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="sub_category">Country</InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select
                                                    id="country"
                                                    name="country"
                                                    value={formik.values.country}
                                                    onChange={formik.handleChange}
                                                    displayEmpty
                                                    renderValue={formik.values.country ? undefined : () => "Select Country"}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: {
                                                            maxHeight: "40vh",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value={null}>Select Country</MenuItem>
                                                    {countriesMapping?.map((country) => (
                                                        <MenuItem 
                                                            key={country.value} 
                                                            value={country.value}
                                                        >
                                                            {country.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="dateFormat">
                                                Date Format
                                                <Tooltip title="This field defines the date format for this custom form. For example, if you select [[YYYY-MM-DD]], all dates in this form will be displayed in that format.">
                                                    <IconButton size="small" sx={{ ml: 0.5 }}>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select
                                                    id="dateFormat"
                                                    name="dateFormat"
                                                    value={formik.values.dateFormat}
                                                    onChange={formik.handleChange}
                                                    displayEmpty
                                                    renderValue={formik.values.dateFormat ? undefined : () => "Select Date Format"}
                                                >
                                                    <MenuItem value={null}>Select Date Format</MenuItem>
                                                     {DateFormatsOptions.map((format) => (
                                                        <MenuItem key={format} value={format}>
                                                            {format} ({today.format(format)})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-8">
                                        <div className="form-group">
                                            <InputLabel>
                                                Subject Line
                                                <Tooltip title="This field defines the matter values to be added in the subject of the generated EML file">
                                                    <IconButton size="small" sx={{ ml: 0.5 }}>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputLabel>

                                            <TextField
                                                fullWidth
                                                size="small"
                                                name="subjectLines"
                                                value={formik.values.subjectLines || ""}
                                                onChange={handleChange}
                                                onKeyDown={handleKeyDown}
                                                placeholder="Enter or select subject lines"
                                                inputRef={inputRef}
                                                sx={{ mb: 1 }}
                                            />

                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {presetOptions.map((option) => (
                                                    <Chip
                                                        key={option}
                                                        label={option}
                                                        clickable
                                                        disabled={usedChips.includes(option)}
                                                        onClick={() => handleChipClick(option)}
                                                    />
                                                ))}
                                            </Stack>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 1,
                                                    color: "#1976d2",
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                    width: "fit-content"
                                                }}
                                                onClick={() => setOpenModal(true)}
                                            >
                                                Add more fields
                                            </Typography>
                                        </div>
                                    </div>

                                    <div className="col-lg-8"></div>
                                    <div className="col-lg-8"></div>
                                    <div className="col-lg-4">
                                        <Button
                                            type="submit"
                                            sx={{width: "100%"}}
                                            className="btn-theme d-block ms-auto"
                                            disabled={uploadedForm === null}
                                            title={
                                                uploadedForm === null ? "Please Upload a form" : ""
                                            }
                                            style={{
                                                cursor:
                                                    uploadedForm === null ? "not-allowed" : "pointer",
                                                pointerEvents: "auto",
                                                opacity: uploadedForm === null ? 0.5 : 1
                                            }}
                                        >
                                            Generate
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Div>
                )}
            </Box>
            <SubjectLinesModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                options={sortedOptions}
                usedChips={usedChips}
                onCheckboxChange={handleCheckboxChange}
            />
        </div>
        
    );
};


export default AddNewForm;